import { useState } from "react";
import { companyInfo, transportInfo } from "@/mocks/homeData";

export default function ContactSection() {
  const [mapHovered, setMapHovered] = useState(false);

  return (
    <section id="contact" className="relative w-full bg-navy-50 py-16 md:py-24">
      <div className="w-full px-4 md:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="animate-on-scroll text-center mb-12 md:mb-16">
            <p className="text-sky-500 text-sm font-semibold tracking-wider uppercase mb-3">
              Contact Us
            </p>
            <h2 className="text-2xl md:text-4xl font-bold text-navy-900">
              연락처
            </h2>
            <p className="text-navy-500 text-sm md:text-base mt-3 max-w-xl mx-auto">
              궁금한 점이 있으시면 언제든지 연락주세요
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
            {/* Left: contact info + transport */}
            <div className="animate-on-scroll delay-100 space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-white rounded-xl p-5 border border-navy-100">
                  <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center mb-3">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-phone-line text-sky-500 text-lg" />
                    </div>
                  </div>
                  <p className="text-xs text-navy-400 mb-1">대표 전화</p>
                  <a
                    href={`tel:${companyInfo.phone}`}
                    className="text-sm font-bold text-navy-900 hover:text-sky-600 transition-colors"
                  >
                    {companyInfo.phone}
                  </a>
                </div>

                <div className="bg-white rounded-xl p-5 border border-navy-100 sm:col-span-2">
                  <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center mb-3">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-smartphone-line text-sky-500 text-lg" />
                    </div>
                  </div>
                  <p className="text-xs text-navy-400 mb-2">담당자 연락</p>
                  <div className="space-y-2">
                    {companyInfo.contacts.map((contact, idx) => (
                      <a
                        key={idx}
                        href={`tel:${contact.phone}`}
                        className="flex items-center justify-between py-1.5 px-3 rounded-lg bg-navy-50 hover:bg-sky-50 transition-colors cursor-pointer group"
                      >
                        <span className="flex items-center gap-2">
                          <span className="text-xs font-medium text-sky-500 bg-sky-100 px-2 py-0.5 rounded-full whitespace-nowrap">
                            {contact.role}
                          </span>
                          <span className="text-sm font-semibold text-navy-900">{contact.name}</span>
                        </span>
                        <span className="text-sm font-bold text-navy-700 group-hover:text-sky-600 transition-colors whitespace-nowrap">
                          {contact.phone}
                        </span>
                      </a>
                    ))}
                  </div>
                </div>

                <div className="bg-white rounded-xl p-5 border border-navy-100">
                  <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center mb-3">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-mail-line text-sky-500 text-lg" />
                    </div>
                  </div>
                  <p className="text-xs text-navy-400 mb-1">이메일</p>
                  <a
                    href={`mailto:${companyInfo.email}`}
                    className="text-sm font-bold text-navy-900 hover:text-sky-600 transition-colors break-all"
                  >
                    {companyInfo.email}
                  </a>
                </div>

                <div className="bg-white rounded-xl p-5 border border-navy-100">
                  <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center mb-3">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-map-pin-line text-sky-500 text-lg" />
                    </div>
                  </div>
                  <p className="text-xs text-navy-400 mb-1">주소</p>
                  <p className="text-sm font-bold text-navy-900 leading-snug">
                    {companyInfo.address}
                  </p>
                </div>
              </div>

              <div className="bg-navy-900 rounded-xl p-5 md:p-6 text-white">
                <h3 className="text-base font-bold mb-4 flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-bus-line text-sky-300" />
                  </div>
                  오시는 길
                </h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-white/50 mb-1">대중교통</p>
                    <p className="text-sm text-white/80">{transportInfo.bus}</p>
                  </div>
                  <div>
                    <p className="text-xs text-white/50 mb-1">자가용</p>
                    <p className="text-sm text-white/80">{transportInfo.car}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Map image + button */}
            <div className="animate-on-scroll delay-200 flex flex-col gap-3">
              <a
                href="https://kko.to/OQ4LeeJRio"
                target="_blank"
                rel="noopener noreferrer"
                className="relative block w-full cursor-pointer"
                style={{ borderRadius: "12px", overflow: "hidden" }}
                onMouseEnter={() => setMapHovered(true)}
                onMouseLeave={() => setMapHovered(false)}
              >
                <img
                  src="https://kcontents.eemo.co.kr/gallery/naver_map.png"
                  alt="한국문화체육진흥원 위치 지도"
                  style={{
                    width: "100%",
                    height: "400px",
                    objectFit: "cover",
                    borderRadius: "12px",
                    transition: "filter 0.25s ease",
                    filter: mapHovered ? "brightness(1.12)" : "brightness(1)",
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    bottom: "12px",
                    right: "12px",
                    background: "rgba(0,0,0,0.6)",
                    color: "#fff",
                    padding: "6px 14px",
                    borderRadius: "20px",
                    fontSize: "13px",
                    pointerEvents: "none",
                  }}
                >
                  클릭하면 카카오맵에서 열립니다
                </div>
              </a>
              <a
                href="https://kko.to/OQ4LeeJRio"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 w-full py-3 px-5 rounded-xl font-semibold text-sm transition-opacity hover:opacity-85 whitespace-nowrap cursor-pointer"
                style={{ backgroundColor: "#FEE500", color: "#191919" }}
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-map-pin-2-fill text-base" />
                </div>
                카카오맵에서 보기
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}